'use client'
import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { Typography } from 'components/Contentful'

const StatsGrid = ({ content }) => {
  const sectionVariationObject = {
    ...(content?.fields?.experiment || {}),
    variant: content?.fields?.experimentVariation,
  }

  const { title, subtitle, config } = useMemoizedContentGetter(content, [
    'title',
    'subtitle',
    'config',
  ])

  const stats = config?.jsonContent?.stats || []
  const backgroundColor = config?.jsonContent?.backgroundColor || '#ffffff'
  const cardBackgroundColor = config?.jsonContent?.cardBackgroundColor || '#F8F9FC'
  const titleColor = config?.jsonContent?.titleColor || '#1a1a1a'
  const subtitleColor = config?.jsonContent?.subtitleColor || '#4a5568'
  const statNumberColor = config?.jsonContent?.statNumberColor || '#1a1a1a'
  const statLabelColor = config?.jsonContent?.statLabelColor || '#1a1a1a'

  return (
    <Section
      className="py-20"
      backgroundColor={backgroundColor}
      data-testid="stats-grid-section"
      sectionVariationObject={sectionVariationObject}
    >
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="text-center flex flex-col items-center mb-12">
          {title && (
            <Typography
              content={title?.textContent}
              variant="headingXl"
              className="mb-4 font-bold leading-tight text-[36px] md:text-[48px] max-w-[750px]"
              style={{ color: titleColor }}
              data-testid="stats-grid-title"
            />
          )}
          {subtitle && (
            <Typography
              content={subtitle?.textContent}
              variant="bodySm"
              className="leading-relaxed text-[16px] max-w-[800px] mx-auto text-content-primary"
              data-testid="stats-grid-subtitle"
            />
          )}
        </div>

        {/* Stats Grid */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-[32px] justify-center">
          {/* First Item - Full Column */}
          {stats[0] && (
            <div className="flex-1 md:max-w-[33.333%]" data-testid="stats-grid-item-0">
              <div
                className="rounded-[12px] p-10 h-full flex flex-col justify-between min-h-[196px]"
                style={{ backgroundColor: cardBackgroundColor }}
              >
                <div className="mb-6">
                  <Typography
                    content={stats[0].number}
                    variant="displayLg"
                    className="font-bold mb-3 text-[40px] md:text-[56px] leading-[140%]"
                    style={{ color: statNumberColor }}
                    data-testid="stats-number-0"
                  />
                  {stats[0].description && (
                    <Typography
                      content={stats[0].description}
                      variant="bodyXs"
                      className="text-[14px] leading-normal mt-2"
                      style={{ color: subtitleColor }}
                      data-testid="stats-description-0"
                    />
                  )}
                </div>
                <Typography
                  content={stats[0].label}
                  variant="bodySm"
                  className="font-normal text-[16px] leading-normal mt-auto"
                  style={{ color: statLabelColor }}
                  data-testid="stats-label-0"
                />
              </div>
            </div>
          )}

          {/* Second Item - Full Column */}
          {stats[1] && (
            <div className="flex-1 md:max-w-[33.333%]" data-testid="stats-grid-item-1">
              <div
                className="rounded-[12px] p-10 h-full flex flex-col justify-between min-h-[196px]"
                style={{ backgroundColor: cardBackgroundColor }}
              >
                <div className="mb-6">
                  <Typography
                    content={stats[1].number}
                    variant="displayLg"
                    className="font-bold mb-3 text-[40px] md:text-[56px] leading-[140%]"
                    style={{ color: statNumberColor }}
                    data-testid="stats-number-1"
                  />
                  {stats[1].description && (
                    <Typography
                      content={stats[1].description}
                      variant="bodyXs"
                      className="text-[14px] leading-normal mt-2"
                      style={{ color: subtitleColor }}
                      data-testid="stats-description-1"
                    />
                  )}
                </div>
                <Typography
                  content={stats[1].label}
                  variant="bodySm"
                  className="font-normal text-[16px] leading-normal mt-auto"
                  style={{ color: statLabelColor }}
                  data-testid="stats-label-1"
                />
              </div>
            </div>
          )}

          {/* Third Column - Two Items Stacked */}
          {(stats[2] || stats[3]) && (
            <div className="flex-1 md:max-w-[33.333%]">
              <div className="flex flex-col gap-4 md:gap-[32px]">
                {/* Third Item */}
                {stats[2] && (
                  <div
                    className="rounded-[12px] p-10 flex flex-col justify-between min-h-[189px]"
                    style={{ backgroundColor: cardBackgroundColor }}
                    data-testid="stats-grid-item-2"
                  >
                    <div className="mb-6">
                      <Typography
                        content={stats[2].number}
                        variant="displayLg"
                        className="font-bold mb-3 text-[40px] md:text-[56px] leading-[140%]"
                        style={{ color: statNumberColor }}
                        data-testid="stats-number-2"
                      />
                      {stats[2].description && (
                        <Typography
                          content={stats[2].description}
                          variant="bodyXs"
                          className="text-[14px] leading-normal mt-2"
                          style={{ color: subtitleColor }}
                          data-testid="stats-description-2"
                        />
                      )}
                    </div>
                    <Typography
                      content={stats[2].label}
                      variant="bodySm"
                      className="font-normal text-[16px] leading-normal mt-auto"
                      style={{ color: statLabelColor }}
                      data-testid="stats-label-2"
                    />
                  </div>
                )}

                {/* Fourth Item */}
                {stats[3] && (
                  <div
                    className="rounded-[12px] p-8 flex flex-col justify-between min-h-[189px]"
                    style={{ backgroundColor: cardBackgroundColor }}
                    data-testid="stats-grid-item-3"
                  >
                    <div className="mb-6">
                      <Typography
                        content={stats[3].number}
                        variant="displayLg"
                        className="font-bold mb-3 text-[40px] md:text-[56px] leading-[140%]"
                        style={{ color: statNumberColor }}
                        data-testid="stats-number-3"
                      />
                      {stats[3].description && (
                        <Typography
                          content={stats[3].description}
                          variant="bodyXs"
                          className="text-[14px] leading-normal mt-2"
                          style={{ color: subtitleColor }}
                          data-testid="stats-description-3"
                        />
                      )}
                    </div>
                    <Typography
                      content={stats[3].label}
                      variant="bodySm"
                      className="font-normal text-[16px] leading-normal mt-auto"
                      style={{ color: statLabelColor }}
                      data-testid="stats-label-3"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}

export { StatsGrid }
export default StatsGrid
